import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import type { Stock, UpdateStockRequest } from '../../../types/stock';
import { MIN_SEARCH_LENGTH } from '../../../utils/searchConstants';

interface EditStockProps {
  open: boolean;
  stock: Stock | null;
  onClose: () => void;
  onSubmit: (stockId: number, data: UpdateStockRequest) => Promise<void>;
}

export default function EditStock({ open, stock, onClose, onSubmit }: EditStockProps) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && stock) {
      setName(stock.name ?? '');
      setError(null);
    }
  }, [open, stock]);

  const handleTransitionEnd = () => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError("Назва складу обов'язкова");
      return;
    }

    if (name.trim().length < MIN_SEARCH_LENGTH) {
      setError(`Назва складу має містити щонайменше ${MIN_SEARCH_LENGTH} символів`);
      return;
    }

    if (!stock) return;

    setLoading(true);
    setError(null);

    try {
      await onSubmit(stock.id, { name: name.trim() });
      onClose();
    } catch (err) {
      console.error('Error updating stock:', err);
      setError('Не вдалося оновити склад. Спробуйте ще раз.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        transition: {
          onEntered: handleTransitionEnd,
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>Редагувати склад</DialogTitle>
        <DialogContent>
          <TextField
            inputRef={inputRef}
            label="Назва складу"
            fullWidth
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(null);
            }}
            error={!!error}
            helperText={error}
            disabled={loading}
            required
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Скасувати
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Збереження...' : 'Зберегти'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
